import { useState } from "react";
import { MapPin, Utensils, ShoppingBag, Clock, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCart } from "@/contexts/CartContext";
import { LOCATIONS } from "@/types/orders";

interface LocationSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

type OrderMode = "local" | "recogida" | "encargo" | null;

const LocationSelectorModal = ({ open, onOpenChange, onConfirm }: LocationSelectorModalProps) => {
  const { setOrderType, setTableNumber, setLocationCode } = useCart();
  const [step, setStep] = useState<"location" | "mode" | "details">("location");
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<OrderMode>(null);
  const [tableInput, setTableInput] = useState("");

  const handleLocationSelect = (locationCode: string) => {
    setSelectedLocation(locationCode);
    setStep("mode");
  };

  const handleModeSelect = (mode: OrderMode) => {
    setSelectedMode(mode);
    if (mode === "local") {
      setStep("details");
    } else if (mode === "recogida") {
      // Para recoger - directly confirm with llevar type
      setLocationCode(selectedLocation!);
      setOrderType("llevar");
      setTableNumber(null);
      onConfirm();
    } else if (mode === "encargo") {
      // Redirect to encargos page
      setLocationCode(selectedLocation!);
      window.location.href = "/encargos";
    }
  };

  const handleConfirmTable = () => {
    if (selectedLocation && tableInput) {
      setLocationCode(selectedLocation);
      setOrderType("mesa");
      setTableNumber(parseInt(tableInput));
      onConfirm();
    }
  };

  const handleBack = () => {
    if (step === "details") {
      setStep("mode");
      setTableInput("");
    } else if (step === "mode") {
      setStep("location");
      setSelectedMode(null);
    }
  };

  const getLocationName = (code: string) => {
    const location = LOCATIONS.find(l => l.code === code);
    return location?.name || code;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-lg p-0 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--card)) 100%)`,
        }}
      >
        {/* Decorative marble texture header */}
        <div 
          className="h-16 w-full relative"
          style={{
            background: `linear-gradient(135deg, hsl(25, 45%, 18%) 0%, hsl(25, 45%, 25%) 50%, hsl(25, 45%, 18%) 100%)`,
            backgroundSize: "200% 200%",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <img 
              src="/favicon.png" 
              alt="Pastelería Reyes" 
              className="h-12 w-12 rounded-full border-2 border-secondary shadow-lg"
            />
          </div>
        </div>

        <div className="p-6">
          <DialogHeader className="text-center mb-4">
            <DialogTitle className="text-xl font-serif text-primary">
              {step === "location" && "¿Dónde estás hoy?"}
              {step === "mode" && "¿Cómo deseas tu pedido?"}
              {step === "details" && "Número de mesa o barra"}
            </DialogTitle>
            <DialogDescription>
              {step === "location" && "Selecciona la sede para personalizar tu experiencia"}
              {step === "mode" && `Sede: ${selectedLocation ? getLocationName(selectedLocation) : ""}`}
              {step === "details" && "Indica dónde te encuentras"}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Location Selection */}
          {step === "location" && (
            <div className="space-y-3">
              {LOCATIONS.map((location) => (
                <Button
                  key={location.code}
                  variant="outline"
                  className="w-full h-auto py-4 px-4 justify-start text-left hover:bg-secondary/10 hover:border-secondary transition-all group"
                  onClick={() => handleLocationSelect(location.code)}
                >
                  <MapPin className="h-5 w-5 mr-3 text-secondary group-hover:scale-110 transition-transform" />
                  <div>
                    <div className="font-semibold text-primary">{location.name}</div>
                    <div className="text-xs text-muted-foreground">{location.address}</div>
                  </div>
                </Button>
              ))}
            </div>
          )}

          {/* Step 2: Order Mode Selection */}
          {step === "mode" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {/* Pedido en Local - GREEN */}
                <Button
                  variant="outline"
                  className={`h-auto py-4 justify-start hover:bg-green-50 hover:border-green-400 transition-all group ${
                    selectedMode === "local" ? "bg-green-50 border-green-400" : ""
                  }`}
                  onClick={() => handleModeSelect("local")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="p-2 rounded-lg bg-green-100 text-green-700">
                      <Utensils className="h-5 w-5" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-primary">Pedido en Local</div>
                      <div className="text-xs text-muted-foreground">Mesa o barra - Consumo inmediato</div>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-green-500" title="Local" />
                  </div>
                </Button>

                {/* Para Recoger - BLUE */}
                <Button
                  variant="outline"
                  className={`h-auto py-4 justify-start hover:bg-blue-50 hover:border-blue-400 transition-all group ${
                    selectedMode === "recogida" ? "bg-blue-50 border-blue-400" : ""
                  }`}
                  onClick={() => handleModeSelect("recogida")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                      <ShoppingBag className="h-5 w-5" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-primary">Para Recoger</div>
                      <div className="text-xs text-muted-foreground">Takeaway - Recógelo cuando quieras</div>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-blue-500" title="Recogida" />
                  </div>
                </Button>

                {/* Encargos Especiales - ORANGE */}
                <Button
                  variant="outline"
                  className={`h-auto py-4 justify-start hover:bg-orange-50 hover:border-orange-400 transition-all group ${
                    selectedMode === "encargo" ? "bg-orange-50 border-orange-400" : ""
                  }`}
                  onClick={() => handleModeSelect("encargo")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-700">
                      <Package className="h-5 w-5" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-primary">Encargos Especiales</div>
                      <div className="text-xs text-muted-foreground">Tartas y productos elaborados</div>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-orange-500" title="Encargo" />
                  </div>
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={handleBack}
              >
                ← Cambiar sede
              </Button>
            </div>
          )}

          {/* Step 3: Table Number for Local orders */}
          {step === "details" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="table" className="text-sm font-medium text-primary">
                  Número de mesa o barra
                </Label>
                <Input
                  id="table"
                  type="number"
                  placeholder="Ej: 5"
                  value={tableInput}
                  onChange={(e) => setTableInput(e.target.value)}
                  className="text-center text-2xl h-14"
                  min={1}
                  max={99}
                  autoFocus
                />
              </div>

              <Button
                size="lg"
                className="w-full h-14 bg-green-600 hover:bg-green-700 text-white"
                disabled={!tableInput}
                onClick={handleConfirmTable}
              >
                Confirmar mesa
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={handleBack}
              >
                ← Volver
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationSelectorModal;
